import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, AlertTriangle, Bot, CheckCircle2, Clock3, Cpu, Database, KeyRound, PlugZap, RefreshCw, Server, Webhook, XCircle,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { count, dateTime, LoadState, Metric, tableClass } from '@/components/ControlCenter';
import { ChartTypeMenu, useChartType } from '@/components/ChartTypeMenu';
import { OverviewChart } from '@/components/OverviewChart';
import { controlCenterService, SystemHealth } from '@/services/control-center.service';
import { useAdminResource } from '@/hooks/useAdminResource';

const PROVIDER_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp', 'google-leads': 'Google lead forms', 'tiktok-leads': 'TikTok leads', 'meta-leads': 'Meta lead ads',
  meta: 'Meta', tiktok: 'TikTok', google: 'Google Ads', sendgrid: 'SendGrid email', mailgun: 'Mailgun email', email: 'Email events',
};
const label = (provider: string) => PROVIDER_LABELS[provider] ?? provider.replace(/[-_]/g, ' ');

const WORKER_LABELS: Record<string, string> = {
  'whatsapp-broadcasts': 'WhatsApp broadcasts', 'whatsapp-campaigns': 'WhatsApp campaigns', 'workflow-delays': 'Workflow delays',
  'lead-follow-ups': 'Lead follow-up reminders', 'task-reminders': 'Task reminders', 'scheduled-bot-flows': 'Scheduled bot flows', 'ad-rules': 'Ad rules',
};

const WORKER_TONE: Record<SystemHealth['workers'][number]['state'], string> = {
  healthy: 'bg-success-soft text-success', waiting: 'bg-info-soft text-info', stale: 'bg-warning-soft text-warning', failing: 'bg-danger-soft text-danger', disabled: 'bg-surface-alt text-text-muted',
};
const WORKER_STATE_LABEL: Partial<Record<SystemHealth['workers'][number]['state'], string>> = { disabled: 'Turned off', waiting: 'Waiting for first run' };

const AI_TONE: Record<string, string> = {
  connected: 'bg-success-soft text-success', configured: 'bg-info-soft text-info', missing: 'bg-surface-alt text-text-muted',
  error: 'bg-danger-soft text-danger', disabled: 'bg-surface-alt text-text-muted',
};

function Panel({ title, detail, icon: Icon, right, children }: { title: string; detail?: string; icon: typeof Activity; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="admin-panel overflow-hidden">
      <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
        <div className="flex items-start gap-3">
          <span className="rounded-lg bg-primary-soft p-2 text-primary"><Icon size={15} /></span>
          <div><h2 className="text-sm font-semibold">{title}</h2>{detail && <p className="mt-0.5 text-xs text-text-muted">{detail}</p>}</div>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

export default function SystemHealthPage() {
  const { data, loading, error, refresh } = useAdminResource(useCallback(() => controlCenterService.system(), []));
  const [chart, setChart] = useChartType('system-webhook-outcomes', 'donut', ['bar', 'pie', 'donut']);
  const workersUp = data?.workers.filter(w => w.state === 'healthy').length ?? 0;
  // A loop that has not had its first run yet (e.g. the hourly ad rules after a restart) is not a problem.
  const workersWatched = data?.workers.filter(w => w.state !== 'disabled' && w.state !== 'waiting').length ?? 0;
  const db = data?.databaseDetail;

  return <>
    <Topbar title="System health" breadcrumb="Platform / System Health"
      right={<Button variant="ghost" size="sm" loading={loading} onClick={refresh} iconLeft={<RefreshCw size={14} />}>Refresh</Button>} />
    <div className="admin-page space-y-4">
      <LoadState loading={loading} error={error} hasData={!!data} retry={refresh} />
      {data && <>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
          <Metric label="API" value="Online" detail={`${data.api.environment} · Node ${data.api.node}`} icon={Server} tone="success" />
          <Metric label="Database" value={db?.status === 'connected' ? `${db.latencyMs ?? '—'} ms` : 'Unavailable'}
            detail={db?.status !== 'connected' ? 'No database round trip' : db.transactions ? `Replica set ${db.replicaSet ?? '(router)'} · transactions on` : 'Standalone — transactions unavailable'}
            icon={Database} tone={db?.status !== 'connected' ? 'danger' : db.transactions ? 'success' : 'warning'} />
          <Metric label="Workers" value={workersWatched ? `${workersUp}/${workersWatched}` : 'No reports'} detail="Healthy background loops (all servers)" icon={Cpu}
            tone={!workersWatched ? 'warning' : workersUp === workersWatched ? 'success' : 'warning'} />
          <Metric label="Webhook failures" value={count(data.webhooks.failed)} detail={`${count(data.webhooks.completed)} delivered · last 24 h`} icon={Webhook} tone={data.webhooks.failed ? 'danger' : 'success'} />
          <Metric label="AI failures" value={count(data.aiFailures.total)} detail="Failed provider requests · last 24 h" icon={Bot} tone={data.aiFailures.total ? 'warning' : 'success'} />
          <Metric label="Needs attention" value={count(data.reconnect.total)} detail="Accounts to reconnect or fix" icon={PlugZap} tone={data.reconnect.total ? 'warning' : 'success'} />
        </div>

        <div className="grid items-start gap-4 xl:grid-cols-3">
          <div className="space-y-4 xl:col-span-2">
            <Panel title="Platform configuration" detail="Required settings present on this server — names only, values are never shown." icon={KeyRound}>
              <div className="divide-y divide-border">
                {data.integrations.map(item => {
                  const complete = item.configured === item.required;
                  return (
                    <div key={item.provider} className="flex flex-wrap items-center gap-3 px-5 py-3">
                      <span className={`rounded-lg p-1.5 ${complete ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'}`}>{complete ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}</span>
                      <div className="min-w-[10rem] flex-1">
                        <p className="text-sm font-semibold">{item.label}</p>
                        <p className="text-[11px] text-text-muted">{item.configured} of {item.required} present</p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {item.keys.map(key => (
                          <span key={key.key} className={`rounded-md px-2 py-0.5 font-mono text-[10px] ${key.present ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'}`}>
                            {key.present ? '✓' : '✕'} {key.key}
                          </span>
                        ))}
                      </div>
                      <Link to={`/integrations?group=${item.provider}`} className="text-xs text-primary">Manage →</Link>
                    </div>
                  );
                })}
              </div>
            </Panel>

            <Panel title="Accounts needing attention" detail="From each account's last saved provider check." icon={PlugZap}>
              {data.reconnect.rows.length ? (
                <div className="overflow-x-auto"><table className={tableClass}>
                  <thead className="bg-surface-alt"><tr><th>Provider</th><th>Company</th><th>Account</th><th>Issue</th><th>Recorded</th></tr></thead>
                  <tbody>{data.reconnect.rows.map((row, index) => (
                    <tr key={`${row.provider}-${row.account}-${index}`}>
                      <td>{label(row.provider)}</td>
                      <td><Link to={`/companies/${row.companyUid}`} className="hover:text-primary">{row.companyName}</Link></td>
                      <td className="max-w-[14rem] truncate">{row.account}</td>
                      <td className={row.reconnect ? 'text-danger' : 'text-warning'}>{row.issue}</td>
                      <td className="text-text-muted">{dateTime(row.at)}</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              ) : <p className="px-5 py-6 text-xs text-text-muted">No saved connection problems.</p>}
            </Panel>

            <Panel title="Background workers" detail="Each running server records its loops; “stale” means no successful run within three intervals." icon={Cpu}>
              {data.workers.length ? (
                <div className="overflow-x-auto"><table className={tableClass}>
                  <thead className="bg-surface-alt"><tr><th>Worker</th><th>Server</th><th>State</th><th>Last success</th><th>Last failure</th></tr></thead>
                  <tbody>{data.workers.map(worker => (
                    <tr key={`${worker.name}-${worker.instance}`}>
                      <td className="font-medium">{WORKER_LABELS[worker.name] ?? worker.name}</td>
                      <td className="font-mono text-[11px] text-text-muted">{worker.instance}</td>
                      <td><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${WORKER_TONE[worker.state]}`}>{WORKER_STATE_LABEL[worker.state] ?? worker.state[0].toUpperCase() + worker.state.slice(1)}</span></td>
                      <td className="text-text-muted">{dateTime(worker.lastSuccessAt)}</td>
                      <td className={worker.state === 'failing' ? 'text-danger' : 'text-text-muted'} title={worker.lastError}>{worker.lastFailureAt ? `${dateTime(worker.lastFailureAt)}${worker.lastError ? ` · ${worker.lastError}` : ''}` : '—'}</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              ) : <p className="px-5 py-6 text-xs text-text-muted">No worker has reported yet. Workers report once a server running them has been up for a minute.</p>}
            </Panel>
          </div>

          <div className="space-y-4">
            <Panel title="Webhook outcomes" detail="Provider deliveries · last 24 hours" icon={Webhook}
              right={<ChartTypeMenu value={chart} onChange={setChart} allowed={['bar', 'pie', 'donut']} label="Webhook outcome chart type" />}>
              <div className="h-48 px-3 pt-3">
                <OverviewChart type={chart} name="Deliveries" data={[
                  { label: 'Delivered', value: data.webhooks.completed, color: 'var(--color-success)' },
                  { label: 'Failed', value: data.webhooks.failed, color: 'var(--color-danger)' },
                  { label: 'Processing', value: data.webhooks.processing, color: 'var(--color-info)' },
                ]} />
              </div>
              <div className="divide-y divide-border border-t border-border">
                {data.webhooks.byProvider.map(row => (
                  <div key={row.provider} className="flex items-center justify-between px-5 py-2.5 text-xs">
                    <span className="font-medium">{label(row.provider)}</span>
                    <span className="tabular-nums text-text-muted">{count(row.completed)} ok · <span className={row.failed ? 'text-danger' : ''}>{count(row.failed)} failed</span></span>
                  </div>
                ))}
                {!data.webhooks.byProvider.length && <p className="px-5 py-4 text-xs text-text-muted">No deliveries in the last 24 hours.</p>}
              </div>
              {!!data.webhooks.recentFailures.length && (
                <div className="border-t border-border px-5 py-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Recent failures</p>
                  <ul className="space-y-1.5 text-xs">{data.webhooks.recentFailures.slice(0, 8).map((failure, index) => (
                    <li key={index} className="flex items-center justify-between gap-2"><span className="flex items-center gap-1.5"><XCircle size={12} className="text-danger" />{label(failure.provider)}</span><span className="text-text-muted">{dateTime(failure.at)}</span></li>
                  ))}</ul>
                </div>
              )}
            </Panel>

            <Panel title="AI providers" detail="Platform keys and their last connection test." icon={Bot} right={<Link to="/ai/providers" className="text-xs text-primary">Manage →</Link>}>
              <div className="divide-y divide-border">
                {data.aiProviders.map(provider => (
                  <div key={provider.provider} className="flex items-center justify-between px-5 py-2.5 text-xs">
                    <span className="font-medium capitalize">{provider.provider}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-text-muted">{provider.lastTestedAt ? `tested ${dateTime(provider.lastTestedAt)}` : 'not tested'}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${AI_TONE[provider.status] ?? AI_TONE.missing}`}>{provider.status}</span>
                    </span>
                  </div>
                ))}
                {!data.aiProviders.length && <p className="px-5 py-4 text-xs text-text-muted">Provider status unavailable.</p>}
              </div>
              {!!data.aiFailures.rows.length && (
                <div className="border-t border-border px-5 py-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Failures · last 24 h</p>
                  <ul className="space-y-1.5 text-xs">{data.aiFailures.rows.map(row => (
                    <li key={`${row.provider}-${row.category}`} className="flex items-center justify-between gap-2"><span className="capitalize">{row.provider} · {row.category.replace(/_/g, ' ')}</span><span className="tabular-nums text-danger">{count(row.count)}</span></li>
                  ))}</ul>
                </div>
              )}
            </Panel>

            <Panel title="Server errors" detail="Unhandled errors on the server answering this page — find the stack trace in the logs by request ID." icon={AlertTriangle}
              right={<span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${data.monitoring.sentry ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'}`}>{data.monitoring.sentry ? 'Sentry on' : 'Sentry off'}</span>}>
              {data.monitoring.recentErrors.length ? (
                <ul className="divide-y divide-border text-xs">{data.monitoring.recentErrors.map((entry, index) => (
                  <li key={`${entry.at}-${index}`} className="px-5 py-2.5">
                    <div className="flex items-center justify-between gap-2"><span className="font-medium text-danger">{entry.name}</span><span className="text-text-muted">{dateTime(entry.at)}</span></div>
                    <p className="mt-0.5 truncate font-mono text-[10px] text-text-muted">{entry.method} {entry.path}{entry.requestId ? ` · ${entry.requestId}` : ''}</p>
                  </li>
                ))}</ul>
              ) : <p className="px-5 py-4 text-xs text-text-muted">No unhandled errors since this server started. Log level: {data.monitoring.logLevel}.</p>}
            </Panel>

            <section className="admin-panel space-y-3 p-5 text-xs">
              <h2 className="flex items-center gap-2 text-sm font-semibold"><Clock3 size={14} className="text-info" /> This server</h2>
              <dl className="grid grid-cols-2 gap-3">
                <div><dt className="text-text-muted">Instance</dt><dd className="mt-1 break-all font-mono text-[11px]">{data.api.instance}</dd></div>
                <div><dt className="text-text-muted">Uptime</dt><dd className="mt-1">{count(Math.floor(data.uptimeSeconds / 3600))}h {Math.floor(data.uptimeSeconds / 60) % 60}m</dd></div>
                <div><dt className="text-text-muted">Memory</dt><dd className="mt-1">{count(data.memoryMb)} MB</dd></div>
                <div><dt className="text-text-muted">Captured</dt><dd className="mt-1">{dateTime(data.asOf)}</dd></div>
              </dl>
              <Link to="/audit-log" className="inline-block font-medium text-primary">Review audit logs →</Link>
            </section>
          </div>
        </div>
      </>}
    </div>
  </>;
}
